import Microgame from '../engine/Microgame.js';

export default class ReactionGame extends Microgame {

    static getPlayback() {
        return {
            title: { fr: `Réflexe !`, en: `React!`, es: `¡Reflejos!` },
            controls: { fr: `Patiente... puis appuie lorsque la cible devient verte`, en: `Wait... then tap when the target turns green`, es: `Espera... y toca cuando el objetivo se ponga verde` },
            objective: { fr: `Attrape la balle au moment fatidique en ayant les meilleurs réflexes possibles`, en: `Catch the ball with your best reflexes`, es: `Atrapa la bola con tus mejores reflejos` },
            icon: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="var(--accent-color)" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="m12 6 2 4 4 2-4 2-2 4-2-4-4-2 4-2Z"/></svg>`,
            actions: [
                { type: 'wait', duration: 1450 },
                { type: 'tap', x: 50, y: 76, duration: 180 },
                { type: 'wait', duration: 900 },
            ],
            loopDuration: 3300
        };
    }
    constructor(engine) {
        super(engine);
        this.pincerLeft = null;
        this.pincerRight = null;
        this.object = null;

        const speedMult = engine.speedMultiplier || 1.0;
        const adj = 1 + (speedMult - 1) * 0.3;
        this.duration = 4500 * (speedMult / adj);

        this.state = 'waiting'; 
        this.y = -50; // Start off-screen
        this.vy = 0;
        this.gravity = 400 + (speedMult - 1) * 350;
        this.pincerY = 0;
        this.isPincerClosed = false;
        this.ballRadius = 15;
        
        // Freeze timer state
        this._isTimerFrozen = false;
        this._frozenTimerValue = 0;
    }

    setup() {
        this.engine.ui.instruction.textContent = "WAIT...";

        const rect = this.container.getBoundingClientRect();
        this.pincerY = rect.height * 0.75;

        // Pincer Left
        this.pincerLeft = document.createElement('div');
        this.pincerLeft.style.position = 'absolute';
        this.pincerLeft.style.width = 'calc(50% - 65px)'; // Accounts for 130px cup
        this.pincerLeft.style.height = '8px';
        this.pincerLeft.style.background = this.primaryColor;
        this.pincerLeft.style.left = '0';
        this.pincerLeft.style.top = `${this.pincerY}px`;
        this.pincerLeft.style.transform = 'translateY(-50%) translateX(-150px)';
        this.pincerLeft.style.transition = 'transform 0.1s cubic-bezier(0.175, 0.885, 0.32, 1.275)';
        this.pincerLeft.style.boxShadow = `0 0 10px ${this.primaryColor}`;
        this.container.appendChild(this.pincerLeft);

        // Cup Head (Left)
        const cupLeft = document.createElement('div');
        cupLeft.style.position = 'absolute';
        cupLeft.style.boxSizing = 'border-box';
        cupLeft.style.right = '-130px';
        cupLeft.style.top = '50%';
        cupLeft.style.transform = 'translateY(-50%)';
        cupLeft.style.width = '130px';
        cupLeft.style.height = '130px';
        cupLeft.style.border = `10px solid ${this.primaryColor}`;
        cupLeft.style.borderRadius = '50%';
        cupLeft.style.clipPath = 'polygon(0 0, 50% 0, 50% 100%, 0 100%)';
        cupLeft.style.boxShadow = `0 0 15px ${this.primaryColor}, inset 0 0 10px ${this.primaryColor}`;
        this.pincerLeft.appendChild(cupLeft);

        // Pincer Right
        this.pincerRight = document.createElement('div');
        this.pincerRight.style.position = 'absolute';
        this.pincerRight.style.width = 'calc(50% - 65px)';
        this.pincerRight.style.height = '8px';
        this.pincerRight.style.background = this.primaryColor;
        this.pincerRight.style.right = '0';
        this.pincerRight.style.top = `${this.pincerY}px`;
        this.pincerRight.style.transform = 'translateY(-50%) translateX(150px)';
        this.pincerRight.style.transition = 'transform 0.1s cubic-bezier(0.175, 0.885, 0.32, 1.275)';
        this.pincerRight.style.boxShadow = `0 0 10px ${this.primaryColor}`;
        this.container.appendChild(this.pincerRight);

        // Cup Head (Right)
        const cupRight = document.createElement('div');
        cupRight.style.position = 'absolute';
        cupRight.style.boxSizing = 'border-box';
        cupRight.style.left = '-130px';
        cupRight.style.top = '50%';
        cupRight.style.transform = 'translateY(-50%)';
        cupRight.style.width = '130px';
        cupRight.style.height = '130px';
        cupRight.style.border = `10px solid ${this.primaryColor}`;
        cupRight.style.borderRadius = '50%';
        cupRight.style.clipPath = 'polygon(50% 0, 100% 0, 100% 100%, 50% 100%)';
        cupRight.style.boxShadow = `0 0 15px ${this.primaryColor}, inset 0 0 10px ${this.primaryColor}`;
        this.pincerRight.appendChild(cupRight);

        // Falling Ball
        this.object = document.createElement('div');
        this.object.style.position = 'absolute';
        this.object.style.width = `${this.ballRadius * 2}px`;
        this.object.style.height = `${this.ballRadius * 2}px`;
        this.object.style.background = this.secondaryColor;
        this.object.style.borderRadius = '50%';
        this.object.style.left = '50%';
        this.object.style.top = '0';
        this.object.style.transform = `translate(-50%, -50%) translateY(${this.y}px)`;
        this.object.style.boxShadow = `0 0 20px ${this.secondaryColor}`;
        this.container.appendChild(this.object);

        // Input
        this.onTap = this.onTap.bind(this);
        this.engine.input.element.addEventListener('pointerdown', this.onTap);

        // Random Start Delay, correctly scaled with difficulty so the ball always has time to fall
        const speedMult = this.engine.speedMultiplier || 1.0;
        const adj = 1 + (speedMult - 1) * 0.3;
        const delay = this.isPlayback ? 1200 : ((600 + Math.random() * 1500) / adj);

        setTimeout(() => {
            if (!this.active) return;
            this.state = 'falling';
            this.engine.ui.instruction.textContent = "NOW!";
        }, delay);
    }

    onTap() {
        if (this.isPincerClosed || !this.active) return;
        this.isPincerClosed = true;
        this._isTimerFrozen = true;
        this._frozenTimerValue = this.engine.timer;

        // Snap Pincers Closed
        this.pincerLeft.style.transform = 'translateY(-50%) translateX(0)';
        this.pincerRight.style.transform = 'translateY(-50%) translateX(0)';

        if (this.state === 'falling') {
            const cupOuterRadius = 65;

            // Pixel-perfect catch: the ball center must be exactly between the top and bottom boundaries of the cage.
            const isCaught = Math.abs(this.y - this.pincerY) <= cupOuterRadius;

            if (isCaught) {
                this.state = 'caught';
                // Physics continues applying in update(), so the ball bounces inside!
                this.engine.ui.instruction.textContent = "GOTCHA!";
                this.object.style.background = this.primaryColor;
                setTimeout(() => this.win(), 1200); // Wait a bit longer to watch it bounce!
            } else if (this.y < this.pincerY - cupOuterRadius) {
                // Ball is strictly above the cage - it will bounce on top of the closed pincers
            } else {
                // Ball is strictly below the cage
                this.state = 'missed';
                this.engine.ui.instruction.textContent = "TOO LATE!";
                this.object.style.background = '#ff0000';
                setTimeout(() => this.lose(), 600);
            }
        }
    }

    update(dt) {
        if (this._isTimerFrozen) {
            this.engine.timer = this._frozenTimerValue;
        }

        if (this.state === 'falling' || this.state === 'missed' || this.state === 'caught') {
            const dtSec = dt / 1000;
            this.vy += this.gravity * dtSec;
            this.y += this.vy * dtSec;

            // Pincer Collision Detection
            if (this.isPincerClosed) {
                const cupOuterRadius = 65;
                const borderThickness = 10;
                const cupInnerRadius = cupOuterRadius - borderThickness; // 37
                
                const pincerTopOuter = this.pincerY - cupOuterRadius;
                const pincerBottomInner = this.pincerY + cupInnerRadius;
                const pincerTopInner = this.pincerY - cupInnerRadius;

                if (this.state === 'caught') {
                    // Bouncing inside the cage!
                    if (this.y + this.ballRadius > pincerBottomInner) {
                        this.y = pincerBottomInner - this.ballRadius;
                        this.vy = -this.vy * 0.6; // Bouncy hit on bottom
                    } else if (this.y - this.ballRadius < pincerTopInner && this.vy < 0) {
                        this.y = pincerTopInner + this.ballRadius;
                        this.vy = -this.vy * 0.4; // Bouncy hit on top ceiling
                    }
                } else if ((this.state === 'falling' || this.state === 'missed') && this.vy > 0) {
                    // Try to bounce on top of the closed pincer from outside
                    if (this.y + this.ballRadius >= pincerTopOuter && this.y - this.ballRadius < pincerTopOuter + (cupOuterRadius * 2)) {
                        if (this.y < this.pincerY) { // Clearly hitting from above
                            this.y = pincerTopOuter - this.ballRadius;
                            this.vy = -this.vy * 0.4;
                            if (this.state === 'falling') {
                                this.state = 'missed';
                                this.engine.ui.instruction.textContent = "TOO EARLY!";
                                this.object.style.background = '#ff0000';
                                setTimeout(() => this.lose(), 600);
                            }
                        }
                    }
                }
            }

            this.object.style.transform = `translate(-50%, -50%) translateY(${this.y}px)`;

            if (this.y > this.container.clientHeight + 50) {
                if (this.state === 'falling') {
                     this.state = 'missed';
                     this.lose();
                }
            }
        }
    }

    cleanup() {
        super.cleanup();
        this.engine.input.element.removeEventListener('pointerdown', this.onTap);
    }
}
